import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { RefreshCw, Mail, Phone, Building2 } from 'lucide-react';

const STATUS_LABELS = {
  new: { label: 'Nuevo', color: 'bg-amber-500/20 text-amber-300' },
  contacted: { label: 'Contactado', color: 'bg-indigo-500/20 text-indigo-300' },
  closed: { label: 'Cerrado', color: 'bg-emerald-500/20 text-emerald-300' },
};

export default function DemoLeadsTab() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/leads', { params: { page: 1, limit: 100 } })
      .then((r) => { setLeads(r.data.leads || []); setTotal(r.data.total || 0); })
      .catch(() => toast.error('Error cargando los leads de la landing'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (leadId, status) => {
    try {
      await api.patch(`/api/leads/${leadId}/status`, { status });
      setLeads((prev) => prev.map((l) => (l.lead_id === leadId ? { ...l, status } : l)));
    } catch {
      toast.error('No se pudo actualizar el estado');
    }
  };

  return (
    <div data-testid="admin-leads-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="text-gray-400 text-sm">{total} solicitudes de demo recibidas desde la landing</p>
        <button data-testid="admin-leads-refresh" onClick={load} className="text-gray-500 hover:text-white transition-colors">
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-900/50">
                <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">Contacto</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">Tienda</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">Plataforma</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">Pedidos/mes</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">Mensaje</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">Estado</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-800/50">
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-4 py-4"><div className="h-4 bg-gray-800 rounded animate-pulse w-24" /></td>
                    ))}
                  </tr>
                ))
              ) : leads.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-500">Aún no hay solicitudes de demo</td></tr>
              ) : leads.map((lead) => {
                const statusInfo = STATUS_LABELS[lead.status] || STATUS_LABELS.new;
                return (
                  <tr key={lead.lead_id} data-testid="admin-lead-row" className="border-b border-gray-800/50 hover:bg-gray-800/20 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-white font-medium">{lead.nombre}</p>
                      <p className="text-gray-500 text-xs flex items-center gap-1"><Mail size={11} />{lead.email}</p>
                      {lead.telefono && <p className="text-gray-500 text-xs flex items-center gap-1"><Phone size={11} />{lead.telefono}</p>}
                    </td>
                    <td className="px-4 py-3 text-gray-300"><span className="flex items-center gap-1"><Building2 size={12} />{lead.empresa}</span></td>
                    <td className="px-4 py-3 text-gray-400">{lead.plataforma || '—'}</td>
                    <td className="px-4 py-3 text-gray-400">{lead.pedidos_mes || '—'}</td>
                    <td className="px-4 py-3 text-gray-400 max-w-xs truncate">{lead.mensaje || '—'}</td>
                    <td className="px-4 py-3">
                      <select
                        data-testid="admin-lead-status-select"
                        value={lead.status}
                        onChange={(e) => handleStatusChange(lead.lead_id, e.target.value)}
                        className={`text-xs font-medium px-2 py-1 rounded-full outline-none bg-gray-800 border border-gray-700 ${statusInfo.color}`}
                      >
                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
